/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { type ApiTaskSettingsModifiers, type TaskModifierType } from '@kaiizen/kai-pro-api';
import { useAllOrgSettings } from '@kaiizen/kai-pro-api-react-state';
import { useCallback, useMemo, useState } from 'react';

import { SettingsHeaderRow } from '@/Client/Widgets/Settings';
import { AddModifierModal, type AddModifierSubmission } from './AddModifierModal';
import { ModifierCard } from './ModifierCard';
import { ModifierCardBody } from './ModifierCardBody';
import {
  getEffectiveModifierLabel,
  MODIFIER_DUPLICATE_LABEL_ERROR,
  MODIFIER_TOTAL_MAX,
  MODIFIER_TYPE_BY_RESERVED_KEY,
  MODIFIER_TYPE_COPY,
  normalizeModifierLabel,
  RESERVED_KEY_BY_MODIFIER_TYPE,
} from './modifierTypeCopy';
import { ORG_SETTINGS_SPACING } from './OrgSettingsStyleLib';
import { type ModifierRecord, useModifierAutosave } from './useModifierAutosave';

/* The mockup's `.stack`, matching Project Settings above it: cards in a column on the page's own
   inset, each carrying its own border rather than sitting inside a panel. */
const CARD_STACK_STYLE = css`
  label: MODIFIERS_CARD_STACK;
  display: flex;
  flex-direction: column;
  gap: 22px;
  padding: 0 ${ORG_SETTINGS_SPACING.gutterX}px 60px;
`;

/** A stored record's type, falling back to what its reserved key implies. */
export const getModifierType = (
  key: string,
  modifier: ApiTaskSettingsModifiers,
): TaskModifierType | undefined => modifier.type ?? MODIFIER_TYPE_BY_RESERVED_KEY[key];

/** Mockup `refreshHeader`: total against the cap, then the enabled/disabled split. */
export const getModifierCounts = (modifiers: ModifierRecord) => {
  const entries = Object.values(modifiers);
  const enabled = entries.filter(modifier => modifier.isEnabled !== false).length;
  return { total: entries.length, enabled, disabled: entries.length - enabled };
};

/**
 * Modifiers, as a section of the Workflow page beneath Project Settings (KAIDEV-4345).
 *
 * Gated behind the `modifierSettings` feature flag by its callers, which also swap the legacy
 * Outputs Tagging block out. The section autosaves rather than carrying save/cancel, so it takes
 * no navigation guard, and its header's one control adds a modifier rather than saving.
 */
export const ModifiersSection = () => {
  const { allOrgSettings } = useAllOrgSettings();

  const modifiers = useMemo(
    () => (allOrgSettings?.taskSettings?.modifiers ?? {}) as ModifierRecord,
    [allOrgSettings?.taskSettings?.modifiers],
  );
  /* The same rule the add modal applies, and for the same reason: two modifiers sharing a name
     are indistinguishable everywhere they appear. A modifier never conflicts with itself, and
     disabled ones still count — their names remain in use on live work. */
  const validateModifier = useCallback((key: string, candidate: ModifierRecord) => {
    const record = candidate[key];
    if (!record) return undefined;
    const name = normalizeModifierLabel(getEffectiveModifierLabel(key, record));
    const clashes = Object.entries(candidate).some(([otherKey, other]) =>
      otherKey !== key && normalizeModifierLabel(getEffectiveModifierLabel(otherKey, other)) === name
    );
    return clashes ? MODIFIER_DUPLICATE_LABEL_ERROR : undefined;
  }, []);

  const { draft, errorByKey, updateModifier, commitModifier, addModifier, focusModifier } = useModifierAutosave(
    modifiers,
    { validate: validateModifier },
  );
  const counts = getModifierCounts(draft);
  const [isAdding, setIsAdding] = useState(false);

  const usedByType = useMemo(() => {
    const used: Record<TaskModifierType, number> = { resident: 0, deferred: 0, display: 0 };
    Object.entries(draft).forEach(([key, modifier]) => {
      const type = getModifierType(key, modifier);
      // Disabled modifiers still count: their names remain in use on live work.
      if (type) used[type] += 1;
    });
    return used;
  }, [draft]);

  const existingLabels = useMemo(
    () => Object.entries(draft).map(([key, modifier]) => getEffectiveModifierLabel(key, modifier)),
    [draft],
  );

  const handleAdd = ({ type, label }: AddModifierSubmission) => {
    /* A financial type takes its reserved key, because its behaviour is resolved downstream by
       that exact tag name — a minted one would be inert while the card showed locked rules saying
       otherwise. Display mints its own, opaque and never derived from the label, since nothing
       downstream reads it. Both key and tag are written explicitly: an empty tag would not fall
       through the normalizer's `??` to the key. */
    const key = RESERVED_KEY_BY_MODIFIER_TYPE[type]
      ?? `modifier_${crypto.randomUUID().replace(/-/g, '').slice(0, 12)}`;
    addModifier({
      key,
      modifierTag: key,
      type,
      displayName: label,
      isEnabledFieldUser: MODIFIER_TYPE_COPY[type].fieldUsersDefault,
    });
    setIsAdding(false);
  };

  return (
    <div>
      {
        /* The same header as Project Settings above, so the two sections read as one page rather
           than as a page with something bolted beneath it. Its action adds a modifier; there is
           no Save, because every edit here is already stored. */
      }
      <SettingsHeaderRow
        actionDisabled={counts.total >= MODIFIER_TOTAL_MAX}
        actionLabel='Add Modifier'
        counts={{
          lead: `${counts.total} modifier${counts.total === 1 ? '' : 's'}`,
          qualifier: `(${MODIFIER_TOTAL_MAX} max)`,
          details: [`${counts.enabled} enabled`, `${counts.disabled} disabled`],
        }}
        countsTestId='modifiers-header-counts'
        hideDefaultActions
        onActionClick={() => setIsAdding(true)}
        title='Modifiers'
      />

      <div css={CARD_STACK_STYLE}>
        {Object.entries(draft).map(([key, modifier]) => {
          const type = getModifierType(key, modifier);
          // A record whose type cannot be resolved is one the normalizer would have dropped;
          // rendering nothing beats inventing a type for it.
          if (!type) return null;
          return (
            <ModifierCard
              key={key}
              errorMessage={errorByKey[key]}
              isEnabled={modifier.isEnabled !== false}
              label={modifier.displayName}
              modifierKey={key}
              onToggleEnabled={enabled => updateModifier(key, { isEnabled: enabled }, { immediate: true })}
              type={type}
            >
              <ModifierCardBody
                fieldUsersEnabled={modifier.isEnabledFieldUser !== false}
                label={modifier.displayName ?? ''}
                onLabelBlur={() => commitModifier(key)}
                onLabelFocus={() => focusModifier(key)}
                onLabelChange={displayName => updateModifier(key, { displayName })}
                onToggleFieldUsers={enabled => updateModifier(key, { isEnabledFieldUser: enabled }, { immediate: true })}
                type={type}
              />
            </ModifierCard>
          );
        })}
      </div>

      {isAdding
        ? (
          <AddModifierModal
            existingLabels={existingLabels}
            onAdd={handleAdd}
            onCancel={() => setIsAdding(false)}
            totalUsed={counts.total}
            usedByType={usedByType}
          />
        )
        : null}
    </div>
  );
};
